import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Logo from '../components/Logo';

const { FiBook, FiFileText, FiMessageCircle, FiArrowRight, FiShield, FiUsers, FiStar } = FiIcons;

// Componente para contador animado
const AnimatedCounter = ({ target, suffix = '', duration = 2, delay = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.3 });
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (!isInView) return;

    let startTime;
    let animationId;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = (currentTime - startTime) / 1000;

      if (elapsed < delay) {
        animationId = requestAnimationFrame(animate);
        return;
      }

      const progress = Math.min((elapsed - delay) / duration, 1);
      
      // Función de easing para aceleración
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      if (target === '∞') {
        setCount('∞');
      } else if (target === '24/7') {
        if (progress < 0.5) {
          const hourProgress = Math.floor(easeOut * 48); // 0-47
          setCount(`${Math.min(hourProgress, 24)}/7`);
        } else {
          setCount('24/7');
        }
      } else {
        const currentCount = Math.floor(easeOut * parseInt(target));
        setCount(currentCount);
      }

      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isInView, target, duration, delay]);

  return (
    <span ref={ref} className="tabular-nums">
      {count}{suffix}
    </span>
  );
};

// Componente para el símbolo infinito animado
const AnimatedInfinity = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.3 });

  return (
    <motion.span
      ref={ref}
      initial={{ scale: 0, rotate: 0 }}
      animate={isInView ? { 
        scale: [0, 1.2, 1], 
        rotate: [0, 180, 360],
        transition: { 
          duration: 2, 
          delay: 0.5,
          ease: "easeOut",
          times: [0, 0.6, 1]
        }
      } : {}}
      className="inline-block tabular-nums"
    >
      ∞
    </motion.span>
  );
};

const Home = () => {
  const features = [
    {
      icon: FiBook,
      title: 'Conceptualización',
      description: 'Aprende los fundamentos del plagio y la propiedad intelectual con videos educativos.',
      color: 'from-blue-500 to-cyan-500',
      link: '/conceptualizacion/plagio'
    },
    {
      icon: null, // Usaremos emoji para juegos
      title: 'Juegos interactivos',
      description: 'Refuerza tu aprendizaje con juegos de memoria y actividades dinámicas.',
      color: 'from-purple-500 to-pink-500',
      link: '/juegos/plagio',
      emoji: '🎮'
    },
    {
      icon: FiFileText,
      title: 'Resúmenes',
      description: 'Consulta definiciones y conceptos clave de forma rápida y organizada.',
      color: 'from-green-500 to-emerald-500',
      link: '/resumen/plagio'
    },
    {
      icon: FiMessageCircle,
      title: 'Pregúntale a PlaPi',
      description: 'Obtén respuestas personalizadas a tus dudas sobre el tema.',
      color: 'from-orange-500 to-red-500',
      link: '/preguntale-a-plapi'
    }
  ];

  const stats = [
    {
      icon: FiShield,
      number: '100',
      suffix: ' %',
      label: 'Contenido confiable',
      type: 'percentage'
    },
    {
      icon: FiUsers,
      number: '24/7',
      suffix: '',
      label: 'Disponible siempre',
      type: 'time'
    },
    {
      icon: FiStar,
      number: '∞',
      suffix: '',
      label: 'Aprendizaje continuo',
      type: 'infinity'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen"
    >
      {/* Hero Section */}
      <section className="relative overflow-hidden py-12 sm:py-16 md:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center relative z-10">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="flex justify-center mb-6 sm:mb-8"
            >
              <div className="relative">
                <Logo />
                <motion.div
                  className="absolute -inset-4 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-full blur-xl"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                />
              </div>
            </motion.div>

            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6"
            >
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                PlaPi
              </span>
            </motion.h1>

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.25, duration: 0.6 }}
              className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 dark:text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-2"
            >
              Tu plataforma interactiva para aprender sobre{' '}
              <span className="font-semibold text-indigo-600 dark:text-indigo-400">plagio</span> y{' '}
              <span className="font-semibold text-purple-600 dark:text-purple-400">propiedad intelectual</span>{' '}
              de manera dinámica y entretenida.
            </motion.p>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4"
            >
              <Link
                to="/conceptualizacion/plagio"
                className="group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 text-sm sm:text-base"
              >
                Comenzar aprendizaje
                <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </Link>

              <Link
                to="/juegos/plagio"
                className="group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-semibold rounded-xl shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transform hover:scale-105 transition-all duration-300 text-sm sm:text-base"
              >
                Explorar juegos
                <span className="ml-2 text-lg sm:text-xl group-hover:rotate-12 transition-transform">🎮</span>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* REFINED ANIMATED CIRCLES - ELEGANT AND FAST */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Círculo 1: Elegante azul-púrpura */}
          <motion.div
            className="absolute w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full opacity-70"
            style={{
              top: '20%',
              left: '8%',
            }}
            animate={{
              x: [0, 30, 0],
              y: [0, -25, 0],
              scale: [1, 1.3, 1],
              background: [
                'linear-gradient(135deg, #667eea, #764ba2)',
                'linear-gradient(135deg, #667eea, #8b5cf6)',
                'linear-gradient(135deg, #6366f1, #764ba2)',
                'linear-gradient(135deg, #667eea, #764ba2)'
              ]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          />

          {/* Círculo 2: Elegante verde-azul */}
          <motion.div
            className="absolute w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full opacity-60"
            style={{
              top: '60%',
              left: '15%',
            }}
            animate={{
              x: [0, -20, 0],
              y: [0, 20, 0],
              scale: [0.8, 1.2, 0.8],
              background: [
                'linear-gradient(135deg, #4facfe, #00f2fe)',
                'linear-gradient(135deg, #43e97b, #38f9d7)',
                'linear-gradient(135deg, #4facfe, #06d6a0)',
                'linear-gradient(135deg, #4facfe, #00f2fe)'
              ]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
              delay: 1
            }}
          />

          {/* Círculo 3: Elegante rosa-naranja */}
          <motion.div
            className="absolute w-14 h-14 sm:w-18 sm:h-18 md:w-22 md:h-22 rounded-full opacity-65"
            style={{
              top: '30%',
              right: '10%',
            }}
            animate={{
              x: [0, -25, 0],
              y: [0, 30, 0],
              scale: [1.1, 0.9, 1.1],
              background: [
                'linear-gradient(135deg, #fa709a, #fee140)',
                'linear-gradient(135deg, #f093fb, #f5576c)',
                'linear-gradient(135deg, #fa709a, #ff8a80)',
                'linear-gradient(135deg, #fa709a, #fee140)'
              ]
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
              delay: 0.5
            }}
          />

          {/* Círculo 4: Elegante indigo-teal (solo desktop) */}
          <motion.div
            className="hidden lg:block absolute w-10 h-10 rounded-full opacity-50"
            style={{
              bottom: '25%',
              right: '20%',
            }}
            animate={{
              x: [0, 15, 0],
              y: [0, -15, 0],
              scale: [0.9, 1.4, 0.9],
              background: [
                'linear-gradient(135deg, #667eea, #764ba2)',
                'linear-gradient(135deg, #4facfe, #00f2fe)',
                'linear-gradient(135deg, #43e97b, #38f9d7)',
                'linear-gradient(135deg, #667eea, #764ba2)'
              ]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
              delay: 2
            }}
          />

          {/* Overlay sutil para profundidad */}
          <motion.div
            className="absolute top-0 left-0 w-full h-full opacity-20"
            animate={{
              background: [
                'radial-gradient(ellipse at top left, rgba(102, 126, 234, 0.1) 0%, transparent 50%)',
                'radial-gradient(ellipse at bottom right, rgba(79, 172, 254, 0.1) 0%, transparent 50%)',
                'radial-gradient(ellipse at center, rgba(67, 233, 123, 0.1) 0%, transparent 50%)',
                'radial-gradient(ellipse at top left, rgba(102, 126, 234, 0.1) 0%, transparent 50%)'
              ]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-20 px-4 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
              ¿Qué puedes hacer en{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                PlaPi
              </span>?
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Descubre todas las herramientas que tenemos para ti
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="group h-full"
              >
                <Link to={feature.link} className="block h-full">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-700 h-full flex flex-col">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      {feature.emoji ? (
                        <span className="text-lg sm:text-2xl">{feature.emoji}</span>
                      ) : (
                        <SafeIcon icon={feature.icon} className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      )}
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed flex-grow">
                      {feature.description}
                    </p>
                    <div className="mt-3 sm:mt-4 flex items-center text-indigo-600 dark:text-indigo-400 group-hover:translate-x-2 transition-transform duration-300">
                      <span className="text-sm font-medium">Explorar</span>
                      <SafeIcon icon={FiArrowRight} className="ml-1 w-3 h-3 sm:w-4 sm:h-4" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section con Contadores Animados */}
      <section className="py-16 sm:py-20 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.15, duration: 0.6 }}
                viewport={{ once: true }}
                className="text-white"
              >
                <div className="flex justify-center mb-3 sm:mb-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <SafeIcon icon={stat.icon} className="w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                </div>
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold mb-1 sm:mb-2">
                  {stat.type === 'infinity' ? (
                    <AnimatedInfinity />
                  ) : (
                    <AnimatedCounter 
                      target={stat.number} 
                      suffix={stat.suffix}
                      duration={2}
                      delay={index * 0.3}
                    />
                  )}
                </div>
                <div className="text-lg sm:text-xl opacity-90">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Elementos decorativos sutiles para stats */}
        <motion.div
          className="absolute top-10 left-10 w-12 h-12 sm:w-16 sm:h-16 rounded-full opacity-20"
          animate={{
            y: [-10, 10, -10],
            x: [-5, 5, -5],
            scale: [0.9, 1.1, 0.9],
            background: [
              'radial-gradient(circle, rgba(255, 255, 255, 0.3), transparent)',
              'radial-gradient(circle, rgba(255, 255, 255, 0.2), transparent)',
              'radial-gradient(circle, rgba(255, 255, 255, 0.3), transparent)'
            ]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }}
        />

        <motion.div
          className="absolute bottom-10 right-10 w-8 h-8 sm:w-12 sm:h-12 rounded-full opacity-15"
          animate={{
            y: [8, -8, 8],
            x: [4, -4, 4],
            rotate: [0, 180, 360],
            background: [
              'radial-gradient(circle, rgba(255, 255, 255, 0.25), transparent)',
              'radial-gradient(circle, rgba(255, 255, 255, 0.15), transparent)',
              'radial-gradient(circle, rgba(255, 255, 255, 0.25), transparent)'
            ]
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut",
            delay: 1
          }}
        />
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 px-4 relative">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
              ¿Listo para comenzar tu aprendizaje?
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-6 sm:mb-8 px-4">
              Aprende sobre plagio y propiedad intelectual
            </p>
            <Link
              to="/conceptualizacion/plagio"
              className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 text-sm sm:text-base"
            >
              Comenzar ahora
              <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
          </motion.div>
        </div>

        {/* Decoración sutil para CTA */}
        <motion.div
          className="absolute top-1/2 left-1/4 w-6 h-6 sm:w-8 sm:h-8 rounded-full opacity-20"
          animate={{
            y: [-8, 8, -8],
            rotate: [0, 120, 240, 360],
            scale: [0.8, 1.2, 0.8],
            background: [
              'radial-gradient(circle, rgba(99, 102, 241, 0.3), transparent)',
              'radial-gradient(circle, rgba(168, 85, 247, 0.3), transparent)',
              'radial-gradient(circle, rgba(99, 102, 241, 0.3), transparent)'
            ]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }}
        />

        <motion.div
          className="absolute bottom-1/4 right-1/4 w-4 h-4 sm:w-6 sm:h-6 rounded-full opacity-15"
          animate={{
            y: [6, -6, 6],
            x: [3, -3, 3],
            rotate: [360, 180, 0],
            background: [
              'linear-gradient(45deg, rgba(245, 101, 101, 0.25), transparent)',
              'linear-gradient(45deg, rgba(59, 130, 246, 0.25), transparent)',
              'linear-gradient(45deg, rgba(245, 101, 101, 0.25), transparent)'
            ]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut",
            delay: 1.5
          }}
        />
      </section>
    </motion.div>
  );
};

export default Home;